import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { tokens } from '../../../theme';
import { addToCart } from '../../../features/cart/cartApiSlice';
import {
  useGetProductByIdQuery,
  useGetProductsQuery,
} from '../../../features/products/productsApiSlice';
import Navbar from '../global/Navbar';
import Footer from '../global/Footer';
import ProductCard from './ProductCard';

const getProductsArray = (productsData) => {
  if (Array.isArray(productsData)) return productsData;
  return productsData?.products || [];
};

const getProductImage = (product) => {
  const image =
    product?.primaryImage ||
    product?.images?.find((item) => item.isPrimary) ||
    product?.images?.[0];

  return image?.url || '';
};

const formatPrice = (product) => {
  if (product?.pricing?.displayPriceText) return product.pricing.displayPriceText;

  const basePrice = product?.pricing?.basePrice;

  if (typeof basePrice === 'number') {
    return `$${new Intl.NumberFormat('en-US').format(basePrice)}`;
  }

  return 'Contact for pricing';
};

const getYouTubeEmbedUrl = (video) => {
  if (!video) return '';

  if (video.embedUrl) return video.embedUrl;

  if (video.youtubeId) {
    return `https://www.youtube.com/embed/${video.youtubeId}`;
  }

  const raw = video.url || '';

  const patterns = [
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtube\.com\/embed\/([^?&]+)/,
    /youtu\.be\/([^?&]+)/,
    /youtube\.com\/shorts\/([^?&]+)/,
  ];

  for (const pattern of patterns) {
    const match = raw.match(pattern);
    if (match?.[1]) return `https://www.youtube.com/embed/${match[1]}`;
  }

  return '';
};

const getPrimaryVideo = (product) => {
  if (!product?.videos?.length) return null;

  return (
    product.videos.find((video) => video.isPrimary) ||
    [...product.videos].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))[0]
  );
};

const getProductPriceNumber = (product) => {
  const basePrice = product?.pricing?.basePrice;

  if (typeof basePrice === 'number') return basePrice;

  const parsedPrice = Number(basePrice);

  return Number.isNaN(parsedPrice) ? 0 : parsedPrice;
};

const buildCartItem = (product, count) => {
  const image =
    product?.primaryImage ||
    product?.images?.find((item) => item.isPrimary) ||
    product?.images?.[0];

  return {
    id: product._id || product.id || product.slug,
    _id: product._id,
    slug: product.slug,
    name: product.name,
    sku: product.sku,
    category: product.category,
    shortDescription: product.shortDescription || product.tagline || '',
    description: product.description || '',
    count,
    price: getProductPriceNumber(product),
    displayPriceText: product?.pricing?.displayPriceText || formatPrice(product),
    imageUrl: image?.url || '',
    images: product.images || [],
    pricing: product.pricing || {},
  };
};

const ItemDetails = () => {
  const dispatch = useDispatch();
  const { itemId } = useParams();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery('(max-width:800px)');

  const [tab, setTab] = useState('overview');
  const [count, setCount] = useState(1);


  const {
    data: product,
    isLoading: isProductLoading,
    isFetching: isProductFetching,
    isError: isProductError,
    error: productError,
  } = useGetProductByIdQuery(itemId);

  const {
    data: productsData,
    isLoading: isRelatedLoading,
  } = useGetProductsQuery({
    status: 'active',
    limit: 100,
  });

  const relatedProducts = useMemo(() => {
    const products = getProductsArray(productsData);

    return products
      .filter((item) => item.slug !== product?.slug && item._id !== product?._id)
      .slice(0, 3);
  }, [productsData, product]);

  const imageUrl = getProductImage(product);

  const primaryVideo = getPrimaryVideo(product);
  const primaryVideoEmbedUrl = getYouTubeEmbedUrl(primaryVideo);

  if (isProductLoading || isProductFetching) {
    return (
      <Box minHeight="100vh">
        <Navbar />
        <Box display="flex" justifyContent="center" py={12}>
          <CircularProgress />
        </Box>
        <Footer />
      </Box>
    );
  }

  if (isProductError || !product) {
    return (
      <Box minHeight="100vh">
        <Navbar />
        <Container maxWidth="md" sx={{ py: 10 }}>
          <Alert severity="error">
            {productError?.data?.message || productError?.message || 'Product not found.'}
          </Alert>
          <Button
            component={RouterLink}
            to="/store"
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 3 }}
          >
            Back to Store
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box width="100%" minHeight="100vh">
      <Navbar />

      <Box
        sx={{
          background:
            'radial-gradient(circle at 20% 10%, rgba(0,255,170,0.16), transparent 30%), linear-gradient(180deg, #071015 0%, #05070a 100%)',
          py: { xs: 4, md: 8 },
        }}
      >
        <Container maxWidth="xl">
          <Button
            component={RouterLink}
            to="/store"
            startIcon={<ArrowBackIcon />}
            sx={{
              color: colors.greenAccent[300],
              mb: 3,
              fontWeight: 700,
            }}
          >
            Back to Store
          </Button>

          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '28px',
                  overflow: 'hidden',
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  minHeight: isMobile ? 320 : 520,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {imageUrl ? (
                  <Box
                    component="img"
                    src={imageUrl}
                    alt={product.name}
                    sx={{
                      width: '100%',
                      height: isMobile ? 320 : 520,
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Typography variant="h3" color={colors.grey[500]} fontWeight="bold">
                    USFuel
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={product.category}
                    sx={{
                      color: colors.greenAccent[300],
                      backgroundColor: 'rgba(0,255,170,0.10)',
                      fontWeight: 800,
                    }}
                  />

                  {product.featured && (
                    <Chip
                      label="Featured"
                      sx={{
                        color: 'white',
                        backgroundColor: 'rgba(104,112,250,0.55)',
                        fontWeight: 800,
                      }}
                    />
                  )}
                </Stack>

                <Typography
                  variant={isMobile ? 'h2' : 'h1'}
                  fontWeight="900"
                  sx={{
                    color: 'white',
                    fontFamily: 'Poppins, sans-serif',
                    letterSpacing: '-0.05em',
                    lineHeight: 0.95,
                  }}
                >
                  {product.name}
                </Typography>

                <Typography
                  variant="h5"
                  sx={{
                    color: colors.greenAccent[300],
                    fontWeight: 800,
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  {product.tagline}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: colors.grey[300],
                    lineHeight: 1.8,
                    maxWidth: '720px',
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  {product.shortDescription || product.description}
                </Typography>

                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: '20px',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.10)',
                  }}
                >
                  <Stack
                    direction={isMobile ? 'column' : 'row'}
                    spacing={2}
                    alignItems={isMobile ? 'stretch' : 'center'}
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography color={colors.grey[400]} variant="body2">
                        Price
                      </Typography>
                      <Typography color="white" variant="h4" fontWeight="900">
                        {formatPrice(product)}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        display="flex"
                        alignItems="center"
                        border="1px solid rgba(255,255,255,0.18)"
                        borderRadius="999px"
                        p="2px 8px"
                      >
                        <IconButton
                          sx={{ color: 'white' }}
                          onClick={() => setCount(Math.max(count - 1, 1))}
                        >
                          <RemoveIcon />
                        </IconButton>

                        <Typography sx={{ px: 1.5, color: 'white', fontWeight: 800 }}>
                          {count}
                        </Typography>

                        <IconButton
                          sx={{ color: 'white' }}
                          onClick={() => setCount(count + 1)}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>

                      <Button
                        variant="contained"
                        onClick={() => dispatch(addToCart({ item: buildCartItem(product, count) }))}
                        sx={{
                          borderRadius: '999px',
                          px: 4,
                          py: 1.5,
                          color: '#06100c',
                          backgroundColor: colors.greenAccent[400],
                          fontWeight: 900,
                          '&:hover': {
                            backgroundColor: colors.greenAccent[300],
                          },
                        }}
                      >
                        Add to Cart
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {(product.features || []).slice(0, 6).map((feature) => (
                    <Chip
                      key={feature.title}
                      icon={<CheckCircleOutlineIcon />}
                      label={feature.title}
                      sx={{
                        color: colors.grey[100],
                        borderColor: 'rgba(255,255,255,0.14)',
                        mb: 1,
                      }}
                      variant="outlined"
                    />
                  ))}
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 5, md: 8 } }}>
        <Tabs
          value={tab}
          onChange={(event, newValue) => setTab(newValue)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons="auto"
          sx={{
            mb: 4,
            '& .MuiTab-root': {
              color: colors.grey[400],
              fontWeight: 800,
            },
            '& .Mui-selected': {
              color: `${colors.greenAccent[300]} !important`,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: colors.greenAccent[400],
            },
          }}
        >
          <Tab label="Overview" value="overview" />
          <Tab label="Features" value="features" />
          <Tab label="Specs" value="specs" />
          <Tab label="Fitment" value="fitment" />
        </Tabs>

        {tab === 'overview' && (
          <Stack spacing={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={primaryVideoEmbedUrl ? 7 : 12}>
                <Paper
                  sx={{
                    p: { xs: 3, md: 4 },
                    height: '100%',
                    borderRadius: '24px',
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.055)',
                    border: '1px solid rgba(255,255,255,0.10)',
                  }}
                >
                  <Typography variant="h3" fontWeight="900" mb={2}>
                    About {product.name}
                  </Typography>

                  <Typography
                    sx={{
                      whiteSpace: 'pre-wrap',
                      color: colors.grey[300],
                      lineHeight: 1.8,
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    {product.description}
                  </Typography>
                </Paper>
              </Grid>

              {primaryVideoEmbedUrl && (
                <Grid item xs={12} lg={5}>
                  <Paper
                    sx={{
                      p: { xs: 2, md: 3 },
                      height: '100%',
                      borderRadius: '24px',
                      color: 'white',
                      backgroundColor: 'rgba(255,255,255,0.055)',
                      border: '1px solid rgba(255,255,255,0.10)',
                    }}
                  >
                    <Typography variant="h4" fontWeight="900" mb={2}>
                      {primaryVideo?.title || 'Product Video'}
                    </Typography>

                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        pt: '56.25%',
                        borderRadius: '18px',
                        overflow: 'hidden',
                        backgroundColor: 'black',
                      }}
                    >
                      <Box
                        component="iframe"
                        src={primaryVideoEmbedUrl}
                        title={primaryVideo?.title || `${product.name} video`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          border: 0,
                        }}
                      />
                    </Box>

                    {primaryVideo?.description && (
                      <Typography color={colors.grey[300]} mt={2} lineHeight={1.7}>
                        {primaryVideo.description}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              )}
            </Grid>

            {product.sections?.length > 0 && (
              <Box>
                <Typography color="white" variant="h3" fontWeight="900" mb={3}>
                  Product Details
                </Typography>

                <Grid container spacing={3}>
                  {product.sections.map((section) => (
                    <Grid item xs={12} md={6} xl={4} key={section.heading}>
                      <Paper
                        sx={{
                          p: 3,
                          height: '100%',
                          borderRadius: '24px',
                          color: 'white',
                          backgroundColor: 'rgba(255,255,255,0.055)',
                          border: '1px solid rgba(255,255,255,0.10)',
                        }}
                      >
                        <Typography variant="h4" fontWeight="900" mb={1}>
                          {section.heading}
                        </Typography>

                        {section.body && (
                          <Typography color={colors.grey[300]} mb={2} lineHeight={1.7}>
                            {section.body}
                          </Typography>
                        )}

                        <List dense>
                          {(section.bullets || []).map((bullet) => (
                            <ListItem key={bullet} sx={{ px: 0, color: colors.grey[200] }}>
                              <CheckCircleOutlineIcon
                                sx={{ mr: 1.5, color: colors.greenAccent[400] }}
                                fontSize="small"
                              />
                              {bullet}
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Stack>
        )}

        {tab === 'features' && (
          <Grid container spacing={3}>
            {(product.features || []).map((feature) => (
              <Grid item xs={12} md={6} lg={4} key={feature.title}>
                <Paper
                  sx={{
                    p: 3,
                    minHeight: 180,
                    borderRadius: '24px',
                    color: 'white',
                    backgroundColor: 'rgba(255,255,255,0.055)',
                    border: '1px solid rgba(255,255,255,0.10)',
                  }}
                >
                  <Typography variant="h4" fontWeight="900" mb={1}>
                    {feature.title}
                  </Typography>
                  <Typography color={colors.grey[300]} lineHeight={1.7}>
                    {feature.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {tab === 'specs' && (
          <Paper
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: '24px',
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.055)',
              border: '1px solid rgba(255,255,255,0.10)',
            }}
          >
            <Grid container spacing={3}>
              {[
                ['Screen Size', product.technicalSpecs?.screenSize],
                ['Power Requirements', product.technicalSpecs?.powerRequirements],
                ['Mounting', product.technicalSpecs?.mounting],
                ['Operating System', product.technicalSpecs?.operatingSystem],
                ['Warranty', product.technicalSpecs?.warranty],
              ]
                .filter(([, value]) => value)
                .map(([label, value]) => (
                  <Grid item xs={12} md={6} key={label}>
                    <Typography color={colors.grey[400]}>{label}</Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {value}
                    </Typography>
                    <Divider sx={{ mt: 2, borderColor: 'rgba(255,255,255,0.10)' }} />
                  </Grid>
                ))}

              {[
                ['Connectivity', product.technicalSpecs?.connectivity],
                ['Supported Exports', product.technicalSpecs?.supportedExports],
                ['Supported Connections', product.technicalSpecs?.supportedConnections],
              ].map(([label, values]) => (
                <Grid item xs={12} md={4} key={label}>
                  <Typography variant="h5" fontWeight="900" mb={1}>
                    {label}
                  </Typography>

                  <List dense>
                    {(values || []).map((value) => (
                      <ListItem key={value} sx={{ px: 0, color: colors.grey[200] }}>
                        <CheckCircleOutlineIcon
                          sx={{ mr: 1.5, color: colors.greenAccent[400] }}
                          fontSize="small"
                        />
                        {value}
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {tab === 'fitment' && (
          <Paper
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: '24px',
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.055)',
              border: '1px solid rgba(255,255,255,0.10)',
            }}
          >
            {product.fitment?.length ? (
              <Grid container spacing={2}>
                {product.fitment.map((fitment) => (
                  <Grid item xs={12} sm={6} md={4} key={fitment.atgModel}>
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: '16px',
                        backgroundColor: 'rgba(0,0,0,0.25)',
                        border: '1px solid rgba(255,255,255,0.10)',
                      }}
                    >
                      <Typography color="white" fontWeight="900">
                        {fitment.label || fitment.atgModel}
                      </Typography>
                      {fitment.notes && (
                        <Typography color={colors.grey[400]} variant="body2" mt={0.5}>
                          {fitment.notes}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography color={colors.grey[300]}>
                This software product does not require a specific ATG console fitment.
              </Typography>
            )}
          </Paper>
        )}

        {!isRelatedLoading && relatedProducts.length > 0 && (
          <Box mt={8}>
            <Typography color="white" variant="h3" fontWeight="900" mb={3}>
              Related Products
            </Typography>

            <Grid container spacing={3}>
              {relatedProducts.map((relatedProduct) => (
                <Grid item xs={12} md={4} key={relatedProduct._id || relatedProduct.slug}>
                  <ProductCard product={relatedProduct} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>

      <Footer />
    </Box>
  );
};

export default ItemDetails;